import { useState, useEffect, useCallback } from 'react'
import { AlertTriangle, Info, AlertCircle, Trash2, RefreshCw, Copy, Check } from 'lucide-react'

const SEVERITY_CONFIG = {
  error: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Ошибка' },
  warn: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Предупреждение' },
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Информация' },
  log: { icon: Info, color: 'text-gray-400', bg: 'bg-gray-500/20', label: 'Лог' },
}

// AGR backend API base URL
const AGR_API_BASE = 'https://agr-test.colnishko.ru/api'

function LogEntry({ log, onCopyLink }) {
  const [copied, setCopied] = useState(false)
  const config = SEVERITY_CONFIG[log.level] || SEVERITY_CONFIG.log
  const Icon = config.icon

  const handleCopy = () => {
    const jsonUrl = `${AGR_API_BASE}/client-logs?session_id=${encodeURIComponent(log.session_id)}`
    navigator.clipboard.writeText(jsonUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Parse page from URL
  const getPageFromUrl = (url) => {
    if (!url) return 'unknown'
    try {
      const parsed = new URL(url)
      return parsed.pathname || '/'
    } catch {
      return url
    }
  }

  // Extract error type from message
  const getErrorType = (message) => {
    if (!message) return 'Unknown'
    if (message.includes('TypeError')) return 'TypeError'
    if (message.includes('ReferenceError')) return 'ReferenceError'
    if (message.includes('SyntaxError')) return 'SyntaxError'
    if (message.includes('NetworkError') || message.includes('fetch')) return 'NetworkError'
    if (message.includes('Unhandled Promise')) return 'Promise Rejection'
    if (message.includes('Cannot read') || message.includes('undefined')) return 'Null/Undefined'
    return 'JS Error'
  }

  return (
    <div className={`glass p-4 rounded-xl ${config.bg} border border-white/10`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${config.bg}`}>
          <Icon size={20} className={config.color} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color}`}>
              {config.label}
            </span>
            <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
              {getErrorType(log.message)}
            </span>
            <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">
              {getPageFromUrl(log.url)}
            </span>
            {log.telegram_user_id && log.telegram_user_id !== 'unknown' && (
              <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">
                TG: {log.telegram_user_id}
              </span>
            )}
          </div>

          <p className="text-sm text-white/90 break-words mb-2 font-mono">
            {log.message}
          </p>

          <div className="flex items-center gap-4 text-xs text-white/50">
            <span>Session: {log.session_id?.substring(0, 20)}...</span>
            <span>{new Date(log.received_at).toLocaleString('ru-RU')}</span>
          </div>
        </div>

        <button
          onClick={handleCopy}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          title="Копировать ссылку на JSON"
        >
          {copied ? (
            <Check size={16} className="text-green-400" />
          ) : (
            <Copy size={16} className="text-white/50" />
          )}
        </button>
      </div>
    </div>
  )
}

export default function ErrorLogs() {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [stats, setStats] = useState({ total: 0, errors: 0, warnings: 0, info: 0 })
  const filterLabels = {
    all: 'Все',
    error: 'Ошибка',
    warn: 'Предупреждение',
    info: 'Информация',
    log: 'Лог',
  }

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' })
      if (filter !== 'all') {
        params.set('level', filter)
      }

      const response = await fetch(`${AGR_API_BASE}/client-logs?${params}`)
      const data = await response.json()

      // Sort by received_at descending (newest first)
      const sortedLogs = [...data].sort((a, b) =>
        new Date(b.received_at) - new Date(a.received_at)
      )

      setLogs(sortedLogs)

      // Calculate stats
      setStats({
        total: data.length,
        errors: data.filter(l => l.level === 'error').length,
        warnings: data.filter(l => l.level === 'warn').length,
        info: data.filter(l => l.level === 'info' || l.level === 'log').length,
      })
    } catch (error) {
      console.error('Failed to fetch logs:', error)
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    fetchLogs()

    if (autoRefresh) {
      const interval = setInterval(fetchLogs, 10000) // Refresh every 10 seconds
      return () => clearInterval(interval)
    }
  }, [fetchLogs, autoRefresh])

  const handleClear = async () => {
    if (!confirm('Очистить все логи?')) return

    try {
      await fetch(`${AGR_API_BASE}/client-logs`, { method: 'DELETE' })
      fetchLogs()
    } catch (error) {
      console.error('Failed to clear logs:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">🔴 Журнал ошибок</h1>
          <p className="text-white/50 mt-1">Мониторинг ошибок клиентских приложений (AGR)</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              autoRefresh ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/50'
            }`}
          >
            <RefreshCw size={16} className={autoRefresh ? 'animate-spin' : ''} />
            Автообновление
          </button>

          <button
            onClick={fetchLogs}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Обновить
          </button>

          <button
            onClick={handleClear}
            className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors flex items-center gap-2"
          >
            <Trash2 size={16} />
            Очистить
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="glass p-4 rounded-xl text-center">
          <div className="text-3xl font-bold">{stats.total}</div>
          <div className="text-sm text-white/50">Всего</div>
        </div>
        <div className="glass p-4 rounded-xl text-center bg-red-500/10">
          <div className="text-3xl font-bold text-red-400">{stats.errors}</div>
          <div className="text-sm text-white/50">Ошибок</div>
        </div>
        <div className="glass p-4 rounded-xl text-center bg-yellow-500/10">
          <div className="text-3xl font-bold text-yellow-400">{stats.warnings}</div>
          <div className="text-sm text-white/50">Предупреждений</div>
        </div>
        <div className="glass p-4 rounded-xl text-center bg-blue-500/10">
          <div className="text-3xl font-bold text-blue-400">{stats.info}</div>
          <div className="text-sm text-white/50">Информация</div>
        </div>
      </div>

      {/* API Endpoint Info */}
      <div className="glass p-4 rounded-xl bg-purple-500/10">
        <h3 className="font-medium mb-2">📡 API Endpoints для Claude Code</h3>
        <p className="text-xs text-white/50 mb-3">Base URL: {AGR_API_BASE}</p>
        <div className="space-y-2 text-sm font-mono">
          <div className="flex items-center gap-2">
            <span className="text-green-400">GET</span>
            <code className="bg-black/30 px-2 py-1 rounded">/client-logs</code>
            <span className="text-white/50">— все логи</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-400">GET</span>
            <code className="bg-black/30 px-2 py-1 rounded">/client-logs?level=error</code>
            <span className="text-white/50">— только ошибки</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-400">GET</span>
            <code className="bg-black/30 px-2 py-1 rounded">/client-logs?session_id=XXX</code>
            <span className="text-white/50">— по сессии</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-red-400">DELETE</span>
            <code className="bg-black/30 px-2 py-1 rounded">/client-logs</code>
            <span className="text-white/50">— очистить логи</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'error', 'warn', 'info', 'log'].map((level) => (
          <button
            key={level}
            onClick={() => setFilter(level)}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === level
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {filterLabels[level]}
          </button>
        ))}
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p className="text-white/50">Загрузка логов...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 glass rounded-xl">
          <div className="text-4xl mb-4">✨</div>
          <p className="text-white/70">Логов пока нет</p>
          <p className="text-white/50 text-sm mt-2">Ошибки клиентских приложений будут отображаться здесь</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log, index) => (
            <LogEntry key={`${log.session_id}-${log.timestamp}-${index}`} log={log} />
          ))}
        </div>
      )}
    </div>
  )
}
